import { memo, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  Circle,
  ListTodo,
  LoaderCircle,
} from "lucide-react";
import type { TodoListSnapshot } from "@lumen/shared";
import { useNarrowScreen } from "../workspace-editor/useNarrowScreen";
import { t } from "../../i18n";

export const TodoPanel = memo(function TodoPanel({
  snapshot,
  readOnly = false,
}: {
  snapshot: TodoListSnapshot;
  readOnly?: boolean;
}) {
  const narrow = useNarrowScreen();
  const [open, setOpen] = useState(() => !readOnly && !narrow);
  const active = snapshot.todos.find((todo) => todo.status === "in_progress");
  const header = (
    <>
      <ListTodo
        size={15}
        className="shrink-0 text-violet-600 dark:text-violet-300"
      />
      <strong className="shrink-0 text-stone-700 dark:text-stone-200">
        {t("Current tasks")}
      </strong>
      <span className="min-w-0 flex-1 truncate text-stone-400">
        {active?.content ?? snapshot.explanation ?? "All tasks complete"}
      </span>
      <span className="shrink-0 rounded-full bg-violet-50 px-2 py-0.5 font-semibold text-violet-700 dark:bg-violet-950/50 dark:text-violet-300">
        {snapshot.completed}/{snapshot.total}
      </span>
      {!readOnly && (
        <ChevronDown
          size={14}
          className={`shrink-0 text-stone-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      )}
    </>
  );
  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-2">
      <div className="overflow-hidden rounded-xl border border-violet-200 bg-white shadow-sm dark:border-violet-900/60 dark:bg-[#171c1a]">
        {readOnly ? (
          <div className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs">
            {header}
          </div>
        ) : (
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
          >
            {header}
          </button>
        )}
        {open && (
          <ol className="max-h-52 space-y-2 overflow-y-auto border-t border-violet-100 p-3 text-xs dark:border-violet-900/50">
            {snapshot.todos.map((todo, index) => (
              <li
                key={`${index}-${todo.content}`}
                className={`flex items-start gap-2.5 ${todo.status === "completed" ? "text-stone-400" : "text-stone-600 dark:text-stone-300"}`}
              >
                {todo.status === "completed" ? (
                  <CheckCircle2
                    size={15}
                    className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400"
                  />
                ) : todo.status === "in_progress" ? (
                  <LoaderCircle
                    size={15}
                    className="mt-0.5 shrink-0 animate-spin text-violet-600 dark:text-violet-300"
                  />
                ) : (
                  <Circle
                    size={15}
                    className="mt-0.5 shrink-0 text-stone-300 dark:text-stone-600"
                  />
                )}
                <span
                  className={todo.status === "completed" ? "line-through" : ""}
                >
                  {todo.content}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
});
